import LoadingTextSkeleton from '@components/skeleton/LoadingTextSkeleton';
import { CellOfRow } from '@components/tables';
import {
    FishCorrectModel,
    FishDisplayModel,
    FishInstallModel,
    H264Profile,
    RateMode,
    RateQuality,
    StreamEnable,
    TvStandards,
    VbrQuality,
    VideoCodec,
} from '@EcamModel/model/SP111VideoGeneral';
import { Stack, TableBody, TableRow } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import {
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionNotSubItem,
    TableHeaderStyle,
} from '../../../ptz/milesight/components/BasicInfoMilesightCameraPTZ';
import useMileSightSP111, { SectionsCollapseSP111 } from '@pages/cameras/hook/useMilesightSP111';
import { useEffect } from 'react';

const tvStandardsMapping: { [key in TvStandards]: string } = {
    [TvStandards.Off]: 'Off',
    [TvStandards.NTSC]: 'NTSC',
    [TvStandards.PAL]: 'PAL',
};

const rateModeMapping: { [key in RateMode]: string } = {
    [RateMode.CBR]: 'Constant Bit Rate (CBR)',
    [RateMode.VBR]: 'Variable Bit Rate (VBR)',
};

const videoCodecMapping: { [key in VideoCodec]: string } = {
    [VideoCodec.H264]: 'H.264',
    [VideoCodec.MJPEG]: 'MJPEG',
    [VideoCodec.H265]: 'H.265',
};

const h264ProfileMapping: { [key in H264Profile]: string } = {
    [H264Profile.BaseProfile]: 'Base',
    [H264Profile.MainProfile]: 'Main',
    [H264Profile.HighProfile]: 'High',
};

const rateQualityMapping: { [key in RateQuality]: string } = {
    [RateQuality.Low]: '25',
    [RateQuality.Medium]: '50',
    [RateQuality.High]: '60',
    [RateQuality.Higher]: '75',
};

const vbrQualityMapping: { [key in VbrQuality]: string } = {
    [VbrQuality.Low]: 'Low',
    [VbrQuality.Medium]: 'Medium',
    [VbrQuality.High]: 'High',
};

const streamEnableMapping: { [key in StreamEnable]: string } = {
    [StreamEnable.Disable]: 'Disable',
    [StreamEnable.Enable]: 'Enable',
};

const fishDisplayModelMapping: { [key in FishDisplayModel]: string } = {
    [FishDisplayModel.Model10]: 'Model 1.0',
    [FishDisplayModel.Model1P]: 'Model 1P',
    [FishDisplayModel.Model2P]: 'Model 2P',
    [FishDisplayModel.Model2R]: 'Model 2R',
    [FishDisplayModel.Model4R]: 'Model 4R',
    [FishDisplayModel.Model1O3R]: 'Model 1O3R',
    [FishDisplayModel.Model1P3R]: 'Model 1P3R',
    [FishDisplayModel.Model1O1P3R]: 'Model 1O1P3R',
};

const fishInstallModelMapping: { [key in FishInstallModel]: string } = {
    [FishInstallModel.Ceiling]: 'Ceiling',
    [FishInstallModel.Wall]: 'Wall',
    [FishInstallModel.Flat]: 'Flat',
};

const fishCorrectModelMapping: { [key in FishCorrectModel]: string } = {
    [FishCorrectModel.MultiChannelMode]: 'Multi-Channel Mode',
    [FishCorrectModel.BundleStreamMode]: 'Bundle Stream Mode',
};

export interface TableRowData {
    id: number;
    label: string;
    value: string | number;
    altValue?: string | number;
}

const tableHeaders: TableHeaderStyle[] = [
    {
        label: 'No.',
        align: 'left',
    },
    {
        label: 'Name',
        align: 'left',
    },
    {
        label: 'General',
        align: 'left',
    },
    {
        label: 'Event',
        align: 'left',
    },
];

export const labels: string[] = [
    'Enable',
    'Video Codec',
    'Frame Size',
    'Maximum Frame Rate',
    'Bit Rate',
    'Smart Stream',
    'Bit Rate Control',
    'Profile',
    'I-frame Interval',
];

type Props = {
    isOpenCollapse: { [key in SectionsCollapseSP111]: boolean };
    handleToggleCollapse: (section: SectionsCollapseSP111) => void;
};

export default function VideoMilesightsCameraSP111(props: Props) {
    const { camera, filteredCamera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const { SP111VideoGeneral, isLoadingMilesightsSP111VideoGeneral } = useMileSightSP111({
        filteredCamera,
        cameraSP111: camera?.SP111CameraId!,
    });

    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    const statusList_FirstStream: TableRowData[] = [
        { id: 1, label: labels[0], value: SP111VideoGeneral?.EventStreamEnable ? 'Yes' : 'No' },
        {
            id: 2,
            label: labels[1],
            value: videoCodecMapping[SP111VideoGeneral?.StreamList?.MainStream?.ProfileCodec ?? 0],
        },
        {
            id: 3,
            label: labels[2],
            value: `${SP111VideoGeneral?.StreamList?.MainStream?.Width ?? ''}*${
                SP111VideoGeneral?.StreamList?.MainStream?.Height ?? ''
            }`,
        },
        {
            id: 4,
            label: labels[3],
            value: `${SP111VideoGeneral?.StreamList?.MainStream?.Framerate ?? 0} Fps`,
            altValue: `${SP111VideoGeneral?.EventStreamFramerate} Fps`,
        },
        {
            id: 5,
            label: labels[4],
            value: `${SP111VideoGeneral?.StreamList?.MainStream?.Bitrate ?? 0} Kbps`,
            altValue: `${SP111VideoGeneral?.EventStreamBitrate} Kbps`,
        },
        {
            id: 6,
            label: labels[5],
            value: SP111VideoGeneral?.StreamList?.MainStream?.SmartStreamEnable ? 'On' : 'Off',
        },
        {
            id: 7,
            label: labels[6],
            value: rateModeMapping[SP111VideoGeneral?.StreamList?.MainStream?.RateMode ?? 0],
        },
        {
            id: 8,
            label: labels[7],
            value: h264ProfileMapping[SP111VideoGeneral?.StreamList?.MainStream?.Profile ?? 0],
        },
        {
            id: 9,
            label: labels[8],
            value: SP111VideoGeneral?.StreamList?.MainStream?.ProfileGop ?? 0,
        },
    ];

    const statusList_SecondaryStream: DataSectionHasSubItem[] = [
        {
            label: labels[0],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={SP111VideoGeneral?.StreamList?.SubStream?.Enable ? 'Yes' : 'No'}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[1],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={videoCodecMapping[SP111VideoGeneral?.StreamList?.SubStream?.ProfileCodec ?? 'N/A']}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[2],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={`${SP111VideoGeneral?.StreamList?.SubStream?.Width} x ${SP111VideoGeneral?.StreamList?.SubStream?.Height}`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[3],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={`${SP111VideoGeneral?.StreamList?.SubStream?.Framerate} fps`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[4],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={`${SP111VideoGeneral?.StreamList?.SubStream?.Bitrate} Kbps`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[5],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={SP111VideoGeneral?.StreamList?.SubStream?.SmartStreamEnable ? 'On' : 'Off'}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[6],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={rateModeMapping[SP111VideoGeneral?.StreamList?.SubStream?.RateMode ?? 'N/A']}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[7],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={h264ProfileMapping[SP111VideoGeneral?.StreamList?.SubStream?.Profile ?? 'N/A']}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[8],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={`${
                        rateQualityMapping[SP111VideoGeneral?.StreamList?.SubStream?.RateQuality ?? 'N/A']
                    } frame (1-120)`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const statusList_TertiaryStream: DataSectionHasSubItem[] = [
        {
            label: labels[0],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={SP111VideoGeneral?.StreamList?.ThirdStream?.Enable ? 'Yes' : 'No'}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[1],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={videoCodecMapping[SP111VideoGeneral?.StreamList?.ThirdStream?.ProfileCodec ?? 'N/A']}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[2],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={`${SP111VideoGeneral?.StreamList?.ThirdStream?.Width} x ${SP111VideoGeneral?.StreamList?.ThirdStream?.Height}`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[3],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={`${SP111VideoGeneral?.StreamList?.ThirdStream?.Framerate} fps`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[4],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={`${SP111VideoGeneral?.StreamList?.ThirdStream?.Bitrate} Kbps`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[5],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={SP111VideoGeneral?.StreamList?.ThirdStream?.SmartStreamEnable ? 'On' : 'Off'}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[6],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={rateModeMapping[SP111VideoGeneral?.StreamList?.ThirdStream?.RateMode ?? 'N/A']}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[7],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={h264ProfileMapping[SP111VideoGeneral?.StreamList?.ThirdStream?.Profile ?? 'N/A']}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: labels[8],
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111VideoGeneral}
                    value={`${
                        rateQualityMapping[SP111VideoGeneral?.StreamList?.ThirdStream?.RateQuality ?? 'N/A']
                    } frame (1-120)`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    return (
        <Stack id="primaryStream" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            {/* Video */}
            <SectionHasSubItemHeader id="" title="Video" onEdit={handleEdit} />

            <SectionNotSubItem
                header="Primary stream"
                tableHeaders={tableHeaders}
                isOpen={isOpenCollapse.primaryStream}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.PrimaryStream);
                }}
                items={statusList_FirstStream || []}
                tableBody={
                    <TableBody>
                        {statusList_FirstStream.map((row) => (
                            <TableRow key={row.id}>
                                <CellOfRow value={row.id} align="left" />
                                <CellOfRow value={row.label} align="left" />
                                <CellOfRow value={row.value} align="left" />
                                <CellOfRow value={row.altValue || row.value} align="left" />
                            </TableRow>
                        ))}
                    </TableBody>
                }
            />

            <SectionNotSubItem
                name="secondaryStream"
                header="Secondary stream"
                statusList={statusList_SecondaryStream}
                isOpen={isOpenCollapse.secondaryStream}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.SecondaryStream);
                }}
            />

            {/* <SectionNotSubItem
                name="tertiaryStream"
                header="Tertiary stream"
                statusList={statusList_TertiaryStream}
                isOpen={isOpenCollapse.tertiaryStream}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.TertiaryStream);
                }}
            /> */}
        </Stack>
    );
}
